import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import { ReactRenderer, type Editor, posToDOMRect } from "@tiptap/react";
import tippy, { type Instance as Tippy } from "tippy.js";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    aiCommand: {
      /**
       * Shows the AI command tooltip.
       *
       * @param command - An optional command to execute when the tooltip is shown.
       * @returns
       */
      showAICommand: (command?: string) => ReturnType;
      /**
       * Dismisses the AI command tooltip.
       *
       * @param config - An optional configuration object.
       * @param config.force - Whether to force the dismissal of the tooltip.
       * @param config.onHide - A function to call when the tooltip is dismissed.
       * @param config.onCancel - A function to call when the tooltip is canceled.
       * @returns
       */
      hideAICommand: (config?: {
        force?: boolean;
        onHide?: () => void;
        onCancel?: () => void;
      }) => ReturnType;
      /**
       * Locks the AI command tooltip, preventing it from being dismissed.
       */
      lockAICommand: () => ReturnType;
      /**
       * Unlocks the AI command tooltip, allowing it to be dismissed.
       */
      unlockAICommand: () => ReturnType;
      /**
       * Inserts AI-generated content at the current cursor position.
       *
       * - Hides the AI command tooltip.
       * - Adds the AI completion at the cursor.
       * - Refocuses the editor.
       */
      insertAIResult: (completion: string) => ReturnType;
      /**
       * Replaces the selected text with AI-generated content.
       *
       * - Hides the AI command tooltip.
       * - Inserts the AI completion in the selected range.
       * - Refocuses the editor.
       */
      replaceAIResult: (completion: string) => ReturnType;
      /**
       * Inserts AI-generated content below the current cursor position.
       *
       * - Hides the AI command tooltip.
       * - Adds the AI completion below the cursor.
       * - Refocuses the editor.
       */
      insertBelowAIResult: (completion: string) => ReturnType;
    };
  }
}

export type AICommandStorage = {
  visible: boolean;
  lock: boolean;
  command: string | null;
  requestConfirm: boolean;
  tippy: Tippy | null;
  decorations: DecorationSet;
  abortController: AbortController | null;
};

export type AICommandOptions = {
  noteId: string;
  component: React.ComponentType<any>;
  confirmDialog: React.ComponentType<any>;
  toolbar: string;
};

/**
 * Creates a selection decoration for highlighting text.
 * @param editor - The Tiptap editor instance.
 * @returns A DecorationSet with the selection highlight.
 */
const createSelectionDecoration = (editor: Editor) => {
  const { from, to } = editor.state.selection;

  const decoration = Decoration.inline(from, to, {
    class: "bg-[Highlight]",
  });

  return DecorationSet.create(editor.state.doc, [decoration]);
};

/**
 * Configures and shows the Tippy tooltip for the AI command.
 * @param editor - The Tiptap editor instance.
 * @param tippy - The Tippy instance.
 * @param options - The extension's options.
 */
const showAICommandTippy = (editor: Editor, tippy: Tippy, options: AICommandOptions) => {
  const toolbar = document.querySelector(options.toolbar) as unknown as HTMLElement | null;

  const { component: Component, noteId } = options;

  const content = new ReactRenderer(Component, {
    editor,
    props: { editor, noteId, variant: toolbar ? "inline" : "floating" },
  });

  tippy.setContent(content.element);

  tippy.setProps({
    appendTo: () => {
      if (toolbar) return toolbar;

      // const scrollableParent = getNearestScrollableParent(editor.view.dom);
      // if (scrollableParent === document.documentElement)
      return document.body;

      // return scrollableParent as HTMLElement;
    },
    getReferenceClientRect() {
      if (toolbar) {
        // Place the tooltip at bottom of the toolbar if it exists
        const rect = toolbar.getBoundingClientRect();

        return {
          ...rect,
          top: rect.bottom,
          bottom: rect.bottom,
          x: rect.left,
          y: rect.bottom,
          width: rect.width,
          height: 0,
        };
      }

      const { view } = editor;
      const { state } = view;
      const { to } = state.selection;

      const rect = posToDOMRect(view, to, to);

      // Otherwise, place the tooltip at the bottom of the current line
      return {
        ...rect,
        left: view.dom.getBoundingClientRect().left,
        x: view.dom.getBoundingClientRect().left,
        width: view.dom.getBoundingClientRect().width,
      };
    },
    onShow(instance) {
      instance.popper.style.width = toolbar
        ? "100%"
        : editor.view.dom.getBoundingClientRect().width + "px";
    },
  });

  tippy.show();
};

/**
 * Hides the AI command Tippy tooltip and resets its properties.
 * @param tippy - The Tippy instance.
 */
const hideAICommandTippy = (tippy: Tippy) => {
  tippy.hide();
  tippy.setContent("");
  tippy.setProps({
    appendTo: "parent",
    getReferenceClientRect: () => new DOMRect(),
    onShow: () => {},
  });
};

export const AICommand = Extension.create<AICommandOptions, AICommandStorage>({
  name: "aiCommand",

  addStorage() {
    return {
      noteId: "",
      visible: false,
      command: null,
      lock: false,
      tippy: null,
      requestConfirm: false,
      decorations: DecorationSet.empty,
      abortController: new AbortController(),
    };
  },

  addOptions() {
    return {
      noteId: "",
      component: () => null,
      confirmDialog: () => null,
      toolbar: "#editor-toolbar",
    };
  },

  onCreate() {
    this.storage.tippy = tippy(this.editor.view.dom, {
      trigger: "manual",
      interactive: true,
      hideOnClick: false,
      placement: "bottom-start",
      maxWidth: "none",
      zIndex: 1000,
      popperOptions: {
        modifiers: [
          {
            name: "offset",
            options: { offset: [0, 0] },
          },
          {
            name: "preventOverflow",
            options: { padding: 0 },
          },
        ],
      },
    });
  },

  addCommands() {
    return {
      showAICommand:
        (command) =>
        ({ commands }) => {
          const { tippy } = this.storage;
          if (!tippy) return false;

          this.storage.command = command || null;
          this.storage.visible = true;
          this.storage.lock = false;

          showAICommandTippy(this.editor, tippy, this.options);

          this.storage.decorations = createSelectionDecoration(this.editor);

          return commands.blur();
        },
      hideAICommand:
        (config) =>
        ({ commands }) => {
          const { force, onHide = () => {}, onCancel = () => {} } = config || {};

          if (!this.storage.visible) return true;
          if (this.storage.requestConfirm) return true;

          const previousFocus = null;
          // const previousFocus = isFocusable(document.activeElement as HTMLElement)
          //   ? (document.activeElement as HTMLElement)
          //   : null;

          previousFocus?.blur();

          const handleHide = () => {
            this.storage.visible = false;
            this.storage.command = null;
            this.storage.requestConfirm = false;

            hideAICommandTippy(this.storage.tippy!);

            this.storage.decorations = DecorationSet.empty;

            onHide();

            return commands.focus();
          };

          const handleCancel = () => {
            this.storage.requestConfirm = false;
            previousFocus?.focus();
            onCancel();
          };

          if (!force && this.storage.lock) {
            this.storage.requestConfirm = true;
            // showConfirmDialog(this.options.confirmDialog, handleHide, handleCancel);

            return true;
          }

          return handleHide();
        },
      lockAICommand: () => () => {
        this.storage.lock = true;
        return true;
      },
      unlockAICommand: () => () => {
        this.storage.lock = false;
        return true;
      },
      insertAIResult:
        (completion) =>
        ({ chain, state }) => {
          return chain()
            .hideAICommand({ force: true })
            .insertMarkdownAt({ from: state.selection.to, to: state.selection.to }, completion)
            .focus()
            .run();
        },
      replaceAIResult:
        (completion) =>
        ({ chain }) => {
          return chain().hideAICommand({ force: true }).insertMarkdown(completion).focus().run();
        },
      insertBelowAIResult:
        (completion) =>
        ({ chain, state }) => {
          return chain()
            .hideAICommand({ force: true })
            .insertMarkdownAt({ from: state.selection.to, to: state.selection.to }, completion)
            .focus()
            .run();
        },
    };
  },

  // addKeyboardShortcuts() {
  //   return {
  //     '/': ({ editor }) => {
  //       const { selection } = editor.state;
  //       const { $from } = selection;

  //       const isParagraphStart =
  //         $from.parent.type.name === 'paragraph' &&
  //         $from.depth === 1 &&
  //         $from.parentOffset === 0 &&
  //         $from.parent.nodeSize === 2;

  //       return isParagraphStart ? editor.commands.showAICommand() : false;
  //     },
  //   };
  // },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey("aiCommandHighlight"),
        state: {
          init: (_, { doc }) => DecorationSet.create(doc, []),
          apply: (tr, old) => {
            const meta = tr.getMeta("decorations");
            return meta ? meta : tr.docChanged ? old.map(tr.mapping, tr.doc) : old;
          },
        },
        props: {
          decorations: () => this.storage.decorations,
        },
      }),
      new Plugin({
        name: new PluginKey("aiCommandClick"),
        props: {
          handleDOMEvents: {
            mousedown: (_view, event) => {
              if (!this.storage.visible) return false;
              event.preventDefault();
              event.stopPropagation();
              this.editor.commands.hideAICommand();
              return true;
            },
          },
        },
      }),
    ];
  },

  onDestroy() {
    this.storage.abortController?.abort();
    this.storage.tippy?.destroy();
  },
});
